import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building, Settings, LogOut, ChevronDown } from 'lucide-react';
import authService from '../../services/authService';

export interface AdminHeaderProps {
  companyId: string;
  companyName: string;
  title: string;
  subtitle: string;
  currentPage?: 'dashboard' | 'settings';
}

const AdminHeader: React.FC<AdminHeaderProps> = ({
  companyId,
  companyName,
  title,
  subtitle,
  currentPage = 'dashboard',
}) => {
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // ユーザーメニューの外側クリックで閉じる
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleNavigateToSettings = () => {
    setShowUserMenu(false);
    navigate(`/admin/${companyId}/settings`);
  };

  const handleLogout = async () => {
    setShowUserMenu(false);
    await authService.signOutUser();
    window.location.reload();
  };

  return (
    <div className="bg-white shadow-sm border-b">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            {/* 左側：タイトル */}
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{title}</h1>
              <p className="text-sm text-gray-600 mt-1">{subtitle}</p>
            </div>

            {/* 右側：企業情報とユーザーメニュー */}
            <div className="relative flex-shrink-0" ref={userMenuRef}>
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-3 bg-green-50 hover:bg-green-100 border border-green-200 hover:border-green-300 rounded-lg px-4 py-2 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
              >
                <Building className="w-5 h-5 text-green-600" />
                <div className="text-left">
                  <div className="text-sm font-medium text-green-800">{companyName}</div>
                  <div className="text-xs text-green-600">
                    {authService.getCurrentCompanyUser()?.displayName ||
                      authService.getCurrentCompanyUser()?.email ||
                      '管理者'}
                  </div>
                </div>
                <ChevronDown
                  className={`w-4 h-4 text-green-600 transition-transform duration-200 ${showUserMenu ? 'rotate-180' : ''}`}
                />
              </button>

              {/* ドロップダウンメニュー */}
              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                  <div className="px-4 py-2 border-b border-gray-100">
                    <div className="text-sm font-medium text-gray-900">{companyName}</div>
                    <div className="text-xs text-gray-500">ID: {companyId}</div>
                  </div>
                  <div className="px-4 py-2 border-b border-gray-100">
                    <div className="text-xs text-gray-500">ログイン中</div>
                    <div className="text-sm text-gray-900">
                      {authService.getCurrentCompanyUser()?.displayName ||
                        authService.getCurrentCompanyUser()?.email ||
                        '管理者'}
                    </div>
                  </div>

                  {/* ナビゲーションメニュー */}
                  <button
                    onClick={handleNavigateToSettings}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <Settings className="w-4 h-4" />
                    組織設定
                  </button>

                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    ログアウト
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminHeader;
